import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SectionKey, SidebarTheme } from '@scrambl/shared';

// Re-export SidebarTheme for backwards compatibility
export type { SidebarTheme } from '@scrambl/shared';

// Define the view mode type
export type ViewMode = 'grid' | 'list';

// Define the time view mode type
export type TimeViewMode = 'week' | 'day' | 'period';

// Scenario detail workspace layout: edit-only, 50/50 split, or results-only.
export type ScenarioWorkspaceView = 'edit' | 'split' | 'results';

// Define the modules that can have view preferences
export type ModuleType = 'projects' | 'entities' | 'tasks' | 'invoices' | 'time';

// Sidebar theme configuration with display info
export interface SidebarThemeConfig {
  id: SidebarTheme;
  name: string;
  lightColor: string;      // Color in light mode
  darkColor: string;       // Color in dark mode
  lightTextColor: 'light' | 'dark';  // Text color in light mode
  darkTextColor: 'light' | 'dark';   // Text color in dark mode (usually 'light' for all dark variants)
}

// Available sidebar themes with their display configuration
// Ordered to match the design palette - top row then bottom row
// Each theme has a light mode color and a darker variant for dark mode
export const SIDEBAR_THEMES: SidebarThemeConfig[] = [
  // Top row - darker/bolder colors
  { id: 'eggshell-indigo', name: 'Eggshell Indigo', lightColor: '#6B8FD4', darkColor: '#163066', lightTextColor: 'dark', darkTextColor: 'light' },
  { id: 'sunny-yolk', name: 'Sunny Yolk', lightColor: '#F5E4A0', darkColor: '#211F0C', lightTextColor: 'dark', darkTextColor: 'light' },
  { id: 'free-range', name: 'Free Range', lightColor: '#EBA96E', darkColor: '#8A4A0F', lightTextColor: 'dark', darkTextColor: 'light' },
  { id: 'herbed-frittata', name: 'Herbed Frittata', lightColor: '#6DC09B', darkColor: '#115F32', lightTextColor: 'dark', darkTextColor: 'light' },
  { id: 'cast-iron-skillet', name: 'Cast Iron Skillet', lightColor: '#1A181B', darkColor: '#0F0E0F', lightTextColor: 'light', darkTextColor: 'light' },
  // Bottom row - lighter/varied colors
  { id: 'pickled-shell', name: 'Pickled Shell', lightColor: '#F0BBCC', darkColor: '#85364F', lightTextColor: 'dark', darkTextColor: 'light' },
  { id: 'purple-hen', name: 'Purple Hen', lightColor: '#B99DE0', darkColor: '#4A2480', lightTextColor: 'dark', darkTextColor: 'light' },
  { id: 'paprika', name: 'Paprika', lightColor: '#D08A8A', darkColor: '#821D1E', lightTextColor: 'dark', darkTextColor: 'light' },
  { id: 'egg-white', name: 'Egg White', lightColor: '#FAFFFD', darkColor: '#212529', lightTextColor: 'dark', darkTextColor: 'light' },
  { id: 'fog', name: 'Fog', lightColor: '#B5B5B8', darkColor: '#3D3D42', lightTextColor: 'dark', darkTextColor: 'light' },
];

// Define the interface for module preferences
export interface ModulePreferences {
  viewMode: ViewMode;
  // We can add more UI preferences here in the future
  // e.g., sortOrder, filterState, etc.
}

// Define the interface for the UI preferences store
export interface UIPreferencesState {
  // State
  modulePreferences: Record<ModuleType, ModulePreferences>;
  collapsedSections: Record<SectionKey, boolean>;
  sidebarOpen: boolean;
  isAdminMode: boolean;
  sidebarTheme: SidebarTheme;
  tauriGlassEnabled: boolean;
  tauriGlassPreferenceSet: boolean;
  timeViewOrgTimezone: boolean; // Whether to view time entries in org timezone vs user timezone
  timeViewMode: TimeViewMode; // Whether to show period, week, or day view in My Time
  scheduleViewTimezone: string; // Timezone override for schedule views (empty = user default)
  onboardingExpanded: Record<string, boolean>; // Per-org expanded override for the onboarding checklist (orgId -> expanded); absent = derived default
  scenarioWorkspaceView: ScenarioWorkspaceView; // Scenario detail layout: edit-only / 50-50 split / results-only. Remembered per user (localStorage).

  // Actions
  setViewMode: (module: ModuleType, viewMode: ViewMode) => void;
  getViewMode: (module: ModuleType) => ViewMode;
  setSectionCollapsed: (section: SectionKey, collapsed: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  setAdminMode: (isAdmin: boolean) => void;
  setSidebarTheme: (theme: SidebarTheme) => void;
  setTauriGlassEnabled: (enabled: boolean) => void;
  setTauriGlassAutoEnabled: (enabled: boolean) => void;
  setTimeViewOrgTimezone: (useOrgTimezone: boolean) => void;
  setTimeViewMode: (mode: TimeViewMode) => void;
  setScheduleViewTimezone: (tz: string) => void;
  setOnboardingExpanded: (orgId: string, expanded: boolean) => void;
  setScenarioWorkspaceView: (view: ScenarioWorkspaceView) => void;
  resetSidebarState: () => void;
  resetModulePreferences: (module: ModuleType) => void;
  resetAllPreferences: () => void;
}

// Default preferences for each module
const defaultModulePreferences: ModulePreferences = {
  viewMode: 'grid',
};

// Default collapsed state for each section (true = collapsed)
const defaultCollapsedSections: Record<SectionKey, boolean> = {
  main: false,
  work: false,
  sales: false,
  operations: false,
  finance: false,
};

// Create the store with persist middleware
export const useUIPreferencesStore = create<UIPreferencesState>()(
  persist(
    (set, get) => ({
      // Initial state
      modulePreferences: {
        projects: { ...defaultModulePreferences },
        entities: { ...defaultModulePreferences },
        tasks: { ...defaultModulePreferences },
        invoices: { ...defaultModulePreferences },
        time: { ...defaultModulePreferences },
      },
      collapsedSections: { ...defaultCollapsedSections },
      sidebarOpen: true,
      isAdminMode: false,
      sidebarTheme: 'cast-iron-skillet',
      tauriGlassEnabled: false,
      tauriGlassPreferenceSet: false,
      timeViewOrgTimezone: false, // Default to user's timezone
      timeViewMode: (() => {
        try {
          const stored = localStorage.getItem('scrambl-ui-preferences');
          if (stored) {
            const mode = JSON.parse(stored)?.state?.timeViewMode;
            if (mode === 'week' || mode === 'day' || mode === 'period') return mode;
            if (mode === 'list') return 'period';
          }
        } catch { /* ignore */ }
        return 'week';
      })(),
      scheduleViewTimezone: '',
      onboardingExpanded: {},
      scenarioWorkspaceView: 'split',

      // Set view mode for a specific module
      setViewMode: (module: ModuleType, viewMode: ViewMode) => {
        set((state) => ({
          modulePreferences: {
            ...state.modulePreferences,
            [module]: {
              ...state.modulePreferences[module],
              viewMode,
            },
          },
        }));
      },
      
      // Get view mode for a specific module
      getViewMode: (module: ModuleType) => {
        return get().modulePreferences[module]?.viewMode || defaultModulePreferences.viewMode;
      },
      
      // Set collapsed state for a specific section
      setSectionCollapsed: (section: SectionKey, collapsed: boolean) => {
        set((state) => ({
          collapsedSections: {
            ...state.collapsedSections,
            [section]: collapsed,
          },
        }));
      },
      
      // Set sidebar open state
      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },
      
      // Set admin mode state
      setAdminMode: (isAdmin: boolean) => {
        set({ isAdminMode: isAdmin });
      },
      
      // Set sidebar theme
      setSidebarTheme: (theme: SidebarTheme) => {
        set({ sidebarTheme: theme });
        // Also update the document attribute for CSS theming
        document.documentElement.setAttribute('data-sidebar-theme', theme);
      },

      setTauriGlassEnabled: (enabled: boolean) => {
        set({ tauriGlassEnabled: enabled, tauriGlassPreferenceSet: true });
      },

      setTauriGlassAutoEnabled: (enabled: boolean) => {
        const state = get();
        if (state.tauriGlassPreferenceSet || state.tauriGlassEnabled === enabled) {
          return;
        }
        set({ tauriGlassEnabled: enabled });
      },

      // Set time timezone view preference
      setTimeViewOrgTimezone: (useOrgTimezone: boolean) => {
        set({ timeViewOrgTimezone: useOrgTimezone });
      },

      // Set time view mode
      setTimeViewMode: (mode: TimeViewMode) => {
        set({ timeViewMode: mode });
      },

      // Set schedule timezone override (empty string = user's timezone)
      setScheduleViewTimezone: (tz: string) => {
        set({ scheduleViewTimezone: tz });
      },

      // Set onboarding checklist expanded state for a specific organization
      setOnboardingExpanded: (orgId: string, expanded: boolean) => {
        set((state) => ({
          onboardingExpanded: { ...state.onboardingExpanded, [orgId]: expanded },
        }));
      },

      setScenarioWorkspaceView: (view: ScenarioWorkspaceView) => {
        set({ scenarioWorkspaceView: view });
      },

      // Reset sidebar state
      resetSidebarState: () => {
        set({
          collapsedSections: { ...defaultCollapsedSections },
          sidebarOpen: true,
          isAdminMode: false,
          sidebarTheme: 'cast-iron-skillet',
          tauriGlassEnabled: false,
          tauriGlassPreferenceSet: false,
        });
        document.documentElement.setAttribute('data-sidebar-theme', 'cast-iron-skillet');
      },
      
      // Reset preferences for a specific module
      resetModulePreferences: (module: ModuleType) => {
        set((state) => ({
          modulePreferences: {
            ...state.modulePreferences,
            [module]: { ...defaultModulePreferences },
          },
        }));
      },
      
      // Reset all preferences
      resetAllPreferences: () => {
        set({
          modulePreferences: {
            projects: { ...defaultModulePreferences },
            entities: { ...defaultModulePreferences },
            tasks: { ...defaultModulePreferences },
            invoices: { ...defaultModulePreferences },
            time: { ...defaultModulePreferences },
          },
          collapsedSections: { ...defaultCollapsedSections },
          sidebarOpen: true,
          isAdminMode: false,
          sidebarTheme: 'cast-iron-skillet',
          tauriGlassEnabled: false,
          tauriGlassPreferenceSet: false,
          timeViewOrgTimezone: false,
          timeViewMode: 'week',
          scheduleViewTimezone: '',
          onboardingExpanded: {},
          scenarioWorkspaceView: 'split',
        });
        document.documentElement.setAttribute('data-sidebar-theme', 'cast-iron-skillet');
      },
    }),
    {
      name: 'scrambl-ui-preferences', // Name for localStorage
      partialize: (state) => ({
        modulePreferences: state.modulePreferences,
        collapsedSections: state.collapsedSections,
        sidebarOpen: state.sidebarOpen,
        isAdminMode: state.isAdminMode,
        sidebarTheme: state.sidebarTheme,
        tauriGlassEnabled: state.tauriGlassEnabled,
        tauriGlassPreferenceSet: state.tauriGlassPreferenceSet,
        timeViewOrgTimezone: state.timeViewOrgTimezone,
        timeViewMode: state.timeViewMode,
        scheduleViewTimezone: state.scheduleViewTimezone,
        onboardingExpanded: state.onboardingExpanded,
        scenarioWorkspaceView: state.scenarioWorkspaceView,
      }), // Only persist these states
      version: 4,
      migrate: (persistedState, version) => {
        const state = persistedState as Partial<UIPreferencesState> | undefined;
        if (!state) return state as UIPreferencesState;

        if (version < 2) {
          return {
            ...state,
            tauriGlassPreferenceSet: typeof state.tauriGlassEnabled === 'boolean',
            scheduleViewTimezone: state.scheduleViewTimezone ?? '',
          } as UIPreferencesState;
        }

        if (version < 3) {
          return {
            ...state,
            tauriGlassPreferenceSet: state.tauriGlassPreferenceSet ?? false,
            scheduleViewTimezone: state.scheduleViewTimezone ?? '',
          } as UIPreferencesState;
        }

        return {
          ...state,
          tauriGlassPreferenceSet: state.tauriGlassPreferenceSet ?? false,
          scheduleViewTimezone: state.scheduleViewTimezone ?? '',
          timeViewMode:
            (state.timeViewMode as string | undefined) === 'list'
              ? 'period'
              : (state.timeViewMode ?? 'week'),
        } as UIPreferencesState;
      },
      onRehydrateStorage: () => (state) => {
        // Apply the sidebar theme to the document when the store is rehydrated
        if (state?.sidebarTheme) {
          document.documentElement.setAttribute('data-sidebar-theme', state.sidebarTheme);
        }
      },
    }
  )
); 
